import {
  type ComponentPropsWithoutRef,
  type FC,
  type FormEventHandler,
  useRef,
} from 'react'

import {
  cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier,
  cartIntermediateAccessorGetSellerData,
  useProductAddToCart,
} from '../../../../../cart-intermediate'
import { EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA } from '../../../../../experiments/eventTracking/EXPERIMENTS_TRACK_EVENT'
import { experimentsTrackEvent } from '../../../../../experiments/eventTracking/experimentsTrackEvent'
import { useRemovePacksizesExperimentShouldHideVariants } from '../../../../../experiments/remove-variants-for-expa-experiment/useRemoveVariantsExperimentShouldHideVariantsForExpa'
import {
  useOpenableControls,
  useRerenderAndExecute,
} from '../../../../../generic-hooks'
import { useGlobalConfigContext } from '../../../../../global-config'
import { gtmDataLayerPushHomeOneAddToCart } from '../../../../../gtm'
import { productAccessorGetVariantCode } from '../../../../model/productAccessor/productAccessorGetVariantCode'
import { ProductPageRxByPrincipleDialog } from '../../../../rx-by-principle'
import { useProductPageContext } from '../../../productPageContext'
import { ProductPagePrescriptionTypeSelectionModal } from '../ProductPagePrescriptionTypeSelectionModal/ProductPagePrescriptionTypeSelectionModal'
import { ProductPagePrivatePrescriptionNotification } from '../ProductPagePrivatePrescriptionNotification/ProductPagePrivatePrescriptionNotification'

import { AddToCartButton } from './ProductAddToCartButton'
import { useBuildAddToCartParams } from './useBuildAddToCartParams'
import { useProductAddToCartCtaShouldShowSelectErxModal } from './useProductAddToCartCtaShouldShowSelectErxModal'

const PRODUCT_ADD_TO_CART_FORM_ID = 'product-add-to-cart-form-id'

const useTrackAddToCartButtonRemoveVariantsExperimentCallback =
  (): (() => void) => {
    const shouldHideVariantsForRemovePackSizeExperiment =
      useRemovePacksizesExperimentShouldHideVariants()
    const globalConfig = useGlobalConfigContext()

    return (): void => {
      if (shouldHideVariantsForRemovePackSizeExperiment) {
        experimentsTrackEvent(
          globalConfig,
          EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA,
        )
      }
    }
  }

type ProductAddToCartCtaProps = Omit<
  ComponentPropsWithoutRef<'form'>,
  'action' | 'id' | 'onSubmit'
>

const useFormSubmitHandler = (params: {
  attributeSelectionControls: ReturnType<
    typeof useProductPageContext
  >['attributeSelectionControls']
  erxTypeSelectModalControls: ReturnType<typeof useOpenableControls>
  handleAddToCart: () => Promise<void>
  rxByPrincipleControls: ReturnType<
    typeof useProductPageContext
  >['rxByPrincipleControls']
  rxByPrincipleDialogControls: ReturnType<typeof useOpenableControls>
  shouldShowErxModalOnAddToCart: boolean
  trackAddToCartButtonRemoveVariantsExperiment: () => void
}): FormEventHandler<HTMLFormElement> => {
  const {
    attributeSelectionControls,
    erxTypeSelectModalControls,
    handleAddToCart,
    rxByPrincipleControls,
    rxByPrincipleDialogControls,
    shouldShowErxModalOnAddToCart,
    trackAddToCartButtonRemoveVariantsExperiment,
  } = params

  const onSubmit: FormEventHandler<HTMLFormElement> = (event): void => {
    if (
      !attributeSelectionControls.attributeNotSelectedErrorControls.validateWhenAddingToCart()
    ) {
      event.preventDefault()

      return
    }
    if (shouldShowErxModalOnAddToCart) {
      event.preventDefault()
      erxTypeSelectModalControls.handleOpen()

      return
    }
    if (
      rxByPrincipleControls.isReasonRequired &&
      !rxByPrincipleControls.selectedReason
    ) {
      event.preventDefault()
      rxByPrincipleDialogControls.handleOpen()

      return
    }
    event.preventDefault()

    trackAddToCartButtonRemoveVariantsExperiment()

    void handleAddToCart()

    gtmDataLayerPushHomeOneAddToCart()
  }

  return onSubmit
}

export const ProductAddToCartCta: FC<ProductAddToCartCtaProps> = (props) => {
  const formRef = useRef<HTMLFormElement>(null)

  const {
    attributeSelectionControls,
    currentOffer,
    currentPrescriptionType,
    product,
    quantitySelectionControls: { quantity },
    rxByPrincipleControls,
  } = useProductPageContext()
  const shouldShowErxModalOnAddToCart =
    useProductAddToCartCtaShouldShowSelectErxModal()
  const erxTypeSelectModalControls = useOpenableControls()
  const { addToCartParams, isPrivatePrescriptionAndCartMetadataHasErx } =
    useBuildAddToCartParams()
  const rxByPrincipleDialogControls = useOpenableControls()
  const rerenderAndExecute = useRerenderAndExecute()
  const trackAddToCartButtonRemoveVariantsExperiment =
    useTrackAddToCartButtonRemoveVariantsExperimentCallback()

  const redpoints = currentOffer
    ? cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier(
        currentOffer,
      )
    : 0

  const sellerData = cartIntermediateAccessorGetSellerData(currentOffer)

  const { handleAddToCart, isMutating } = useProductAddToCart({
    currentPrescriptionType,
    offerId: currentOffer?.offerId,
    product,
    quantity,
    redpoints,
    rxByPrincipleSelectedReason: rxByPrincipleControls.isReasonRequired
      ? rxByPrincipleControls.selectedReason
      : undefined,
    sellerData,
  })

  const onSubmit = useFormSubmitHandler({
    attributeSelectionControls,
    erxTypeSelectModalControls,
    handleAddToCart,
    rxByPrincipleControls,
    rxByPrincipleDialogControls,
    shouldShowErxModalOnAddToCart,
    trackAddToCartButtonRemoveVariantsExperiment,
  })

  return (
    <>
      <form
        {...props}
        id={PRODUCT_ADD_TO_CART_FORM_ID}
        onSubmit={onSubmit}
        ref={formRef}
      >
        {Object.entries(
          (addToCartParams as unknown as Record<string, string> | undefined) ??
            {},
        ).map(([name, value]) => {
          return <input key={name} name={name} type="hidden" value={value} />
        })}
        <AddToCartButton loading={isMutating} />
        {isPrivatePrescriptionAndCartMetadataHasErx ? (
          <ProductPagePrivatePrescriptionNotification />
        ) : null}
      </form>
      <ProductPagePrescriptionTypeSelectionModal
        controls={erxTypeSelectModalControls}
      />
      {rxByPrincipleControls.isReasonRequired ? (
        <ProductPageRxByPrincipleDialog
          autoOpenOnLocationHash
          key={productAccessorGetVariantCode(product)}
          onClose={rxByPrincipleDialogControls.handleClose}
          onOpen={rxByPrincipleDialogControls.handleOpen}
          onReasonSelect={(reason) => {
            rxByPrincipleDialogControls.handleClose()
            rxByPrincipleControls.setSelectedReason(reason)
            rerenderAndExecute(() => {
              formRef.current?.requestSubmit()
            })
          }}
          open={rxByPrincipleDialogControls.isOpen}
          productName={product.name}
          reasonSet={rxByPrincipleControls.reasonSet}
        />
      ) : null}
    </>
  )
}
